import {getUserTokenOrRedirect} from "../../shared/services/auth.service";
import {NavigateFunction} from "react-router-dom";
import { WorkOrderDto, WorkOrderUpdateDto, WorkOrderUpdateStatusDto } from "./dtos/work-order.dto";
import { savaquanFetch, savaquanFetchJson } from "../../shared/services/http.service";
import { SavaquanHttpError } from "../../shared/utils/http-error";

export async function getWorkOrder(navigate: NavigateFunction, workOrderId: number): Promise<WorkOrderDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const workOrder = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}`,
    {headers: {Authorization: `Bearer ${token}`}}).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch job: ${response.statusText}`);
      }
      return await response.json() as WorkOrderDto
    });
  return workOrder;
}

export async function getWorkOrders(navigate: NavigateFunction): Promise<WorkOrderDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const workOrders = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.statusText}`);
      }
      return await response.json() as WorkOrderDto[]
    });
  return workOrders;
}

export async function updateWorkOrder(navigate: NavigateFunction, workOrderId: number, updateDto: WorkOrderUpdateDto): Promise<WorkOrderDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const updatedWorkOrder = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateDto)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to update job: ${response.statusText}`);
      }
      return await response.json() as WorkOrderDto
    });
  return updatedWorkOrder;
}

export async function updateWorkOrderStatus(navigate: NavigateFunction, workOrderId: number, updateDto: WorkOrderUpdateStatusDto): Promise<WorkOrderDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const updatedWorkOrder = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}/status`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateDto)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to update job status: ${response.statusText}`);
      }
      return await response.json() as WorkOrderDto
    });
  return updatedWorkOrder;
}


/**
 * SQ-559: the server refuses finishing a job, or saving a finished one's crew
 * time, when only some of the crew have time (409 `crew_time_partial`, with
 * the names of those who have none). Read defensively: the names may come as
 * `names` or `crewMembers`, or only in `message`.
 */
export async function crewTimePartialMessage(response: Response): Promise<string | undefined> {
  if (response.status !== 409) return undefined;
  const body = await response.clone().json().catch(() => undefined) as Record<string, unknown> | undefined;
  const code = body?.code ?? body?.error ?? body?.title;
  if (code !== 'crew_time_partial') return undefined;
  const raw = body?.names ?? body?.crewMembers;
  const names = Array.isArray(raw) ? raw.map(n => (typeof n === 'string' ? n : (n as {name?: string})?.name)).filter((n): n is string => !!n) : [];
  if (names.length === 0) {
    return typeof body?.message === 'string' && body.message ? body.message : 'Crew time missing: every crew member needs time on this job, or none.';
  }
  const list = names.length < 2 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Crew time missing: ${list} ${names.length === 1 ? 'has' : 'have'} no time on this job.`;
}

export async function completeWorkOrder(navigate: NavigateFunction, workOrderId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate)
  await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}/complete`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(async response => {
      if (!response.ok) {
        throw new Error(await crewTimePartialMessage(response) ?? `Failed to complete job: ${response.statusText}`);
      }
    });
}

export async function completeWorkOrderAndMarkAsPaid(navigate: NavigateFunction, workOrderId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate)
  await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}/complete-and-mark-as-paid`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(async response => {
      if (!response.ok) {
        throw new Error(await crewTimePartialMessage(response) ?? `Failed to complete job: ${response.statusText}`);
      }
    });
}

export async function createInvoiceAndSend(navigate: NavigateFunction, workOrderId: number): Promise<void> {
  return await savaquanFetchJson<void>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}/create-invoice-and-send`,
    {
      navigate,
      method: 'POST',
    });
}

// ---- The job record (SQ-597): Android's WorkOrderApi ------------------------------------------

const workOrderUrl = (workOrderId: number, path = '') =>
  `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-orders/${workOrderId}${path}`;

/** SQ-393: saves the schedule; the server moves the status with it (Scheduled, off hold). */
export async function updateWorkOrderSchedule(navigate: NavigateFunction, workOrderId: number,
                                              schedule: {scheduledDate: string | null; scheduledStartTime: string | null}): Promise<WorkOrderDto> {
  return await savaquanFetchJson<WorkOrderDto>(workOrderUrl(workOrderId, '/schedule'), {
    navigate, method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(schedule),
  });
}

/** SQ-393 Cancel Schedule: both halves go, and a Scheduled job falls back to Confirmed. */
export async function clearWorkOrderSchedule(navigate: NavigateFunction, workOrderId: number): Promise<WorkOrderDto> {
  return await savaquanFetchJson<WorkOrderDto>(workOrderUrl(workOrderId, '/schedule'), {navigate, method: 'DELETE'});
}

/** SQ-530: each operation's share of the crew's time, total labor in seconds. 204, no body. */
export async function saveJobAllocation(navigate: NavigateFunction, workOrderId: number,
                                        operations: {operationId: number; actualSeconds: number}[]): Promise<void> {
  await savaquanFetch(workOrderUrl(workOrderId, '/allocation'), {
    navigate, method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({operations}),
  });
}

/** The job's active invoice, or undefined when it has none (404). */
export async function getActiveInvoiceId(navigate: NavigateFunction, workOrderId: number): Promise<number | undefined> {
  try {
    const invoice = await savaquanFetchJson<{id: number}>(workOrderUrl(workOrderId, '/invoice'), {navigate});
    return invoice.id;
  } catch (e) {
    if (e instanceof SavaquanHttpError && e.statusCode === 404) return undefined;
    throw e;
  }
}

/** Cuts the invoice from a completed job at once: no confirmation, no send. */
export async function createJobInvoice(navigate: NavigateFunction, workOrderId: number): Promise<{id: number}> {
  return await savaquanFetchJson<{id: number}>(workOrderUrl(workOrderId, '/create-invoice'), {navigate, method: 'POST'});
}
